import { useEffect, useState } from 'react'

const NY_TZ = 'America/New_York'
const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TZ,
  hour12: false,
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

const DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: NY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const DATE_WD_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
})

const NY_HOURS_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TZ,
  hour: 'numeric',
  hour12: false,
})

const MS_PER_DAY = 24 * 60 * 60 * 1000
const WEEKDAY_MAP: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
}

function nyDatePartsOf(ms: number): { y: number; m: number; d: number; weekday: number } {
  const parts = DATE_WD_FMT.formatToParts(ms)
  let y = 0, m = 0, d = 0, wd = ''
  for (const p of parts) {
    if (p.type === 'year') y = parseInt(p.value, 10)
    if (p.type === 'month') m = parseInt(p.value, 10)
    if (p.type === 'day') d = parseInt(p.value, 10)
    if (p.type === 'weekday') wd = p.value
  }
  return { y, m, d, weekday: WEEKDAY_MAP[wd] ?? 0 }
}

/**
 * UTC epoch ms for `hour:minute` NY wall-clock on the given calendar date.
 * Handles DST by guessing EST first (UTC-5) and snapping to EDT (UTC-4) if
 * the resulting instant lands at the wrong NY hour. `month` is 1-based.
 */
function nyWallClockToUtcMs(y: number, month: number, d: number, hour: number, minute: number): number {
  const estGuess = Date.UTC(y, month - 1, d, hour + 5, minute)
  const guessHour = parseInt(
    NY_HOURS_FMT.formatToParts(estGuess).find((p) => p.type === 'hour')?.value ?? '-1',
    10,
  )
  // Intl returns 24 for midnight in some locales; normalize.
  const got = guessHour === 24 ? 0 : guessHour
  if (got === hour) return estGuess
  return Date.UTC(y, month - 1, d, hour + 4, minute)
}

/**
 * Returns `{ startMs, endMs }` for the previous trading day's extended NY
 * session (04:00 → 20:00 ET). Stepping back day-by-day from today's NY
 * calendar date until we hit a Mon–Fri. Holidays are not detected — if the
 * previous weekday was a holiday, the bar window will be empty and consumers
 * should fall through gracefully.
 */
export function previousTradingSessionWindow(now: Date = new Date()): { startMs: number; endMs: number } {
  const nowMs = now.getTime()
  for (let stepDays = 1; stepDays <= 7; stepDays++) {
    const candidateMs = nowMs - stepDays * MS_PER_DAY
    const parts = nyDatePartsOf(candidateMs)
    if (parts.weekday >= 1 && parts.weekday <= 5) {
      return {
        startMs: nyWallClockToUtcMs(parts.y, parts.m, parts.d, 4, 0),
        endMs: nyWallClockToUtcMs(parts.y, parts.m, parts.d, 20, 0),
      }
    }
  }
  // Unreachable in practice (a 7-day span always contains a weekday).
  return { startMs: nowMs - MS_PER_DAY, endMs: nowMs }
}

/**
 * UTC epoch ms for every regular-session boundary (09:30 open, 16:00 close)
 * that falls within `[startMs, endMs]`. Walks NY calendar days inclusively
 * from start to end; skips Sat/Sun. Holidays are not detected — caller can
 * filter against the actual bars if needed.
 */
export function sessionBoundariesInRange(
  startMs: number,
  endMs: number,
): Array<{ ms: number; kind: 'open' | 'close' }> {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return []
  const out: Array<{ ms: number; kind: 'open' | 'close' }> = []
  // Walk one NY-calendar day at a time. Step is 12h so DST transitions can't
  // skip a day. Dedupe by (y,m,d).
  const seen = new Set<string>()
  for (let cursor = startMs - MS_PER_DAY; cursor <= endMs + MS_PER_DAY; cursor += MS_PER_DAY / 2) {
    const parts = nyDatePartsOf(cursor)
    const key = `${parts.y}-${parts.m}-${parts.d}`
    if (seen.has(key)) continue
    seen.add(key)
    if (parts.weekday < 1 || parts.weekday > 5) continue
    const openMs = nyWallClockToUtcMs(parts.y, parts.m, parts.d, 9, 30)
    const closeMs = nyWallClockToUtcMs(parts.y, parts.m, parts.d, 16, 0)
    if (openMs >= startMs && openMs <= endMs) out.push({ ms: openMs, kind: 'open' })
    if (closeMs >= startMs && closeMs <= endMs) out.push({ ms: closeMs, kind: 'close' })
  }
  out.sort((a, b) => a.ms - b.ms)
  return out
}

export const OPTIONS_CUTOFF_KEY = 'options-cutoff-time'
export const OPENING_CUTOFF_KEY = 'opening-cutoff-time'

// ---- Authoritative market calendar overlay (Schwab /markets) ----
// Populated daily by useMarketCalendar(), keyed by ET date "YYYY-MM-DD". When
// a day is present it OVERRIDES the NY-clock heuristics below, so holidays
// (isOpen:false) and half-days (early close via the real regular-session end)
// are handled correctly. When absent — not yet fetched, or no Schwab account —
// the clock logic is used as a best-effort fallback.
export interface MarketCalendarSession {
  start: string // ISO 8601 with tz offset
  end: string
}
export interface MarketCalendarDay {
  isOpen: boolean
  pre: MarketCalendarSession | null
  regular: MarketCalendarSession | null
  post: MarketCalendarSession | null
}

const marketCalendar = new Map<string, MarketCalendarDay>()

/** Store a normalized calendar day (called by useMarketCalendar on fetch). */
export function setMarketCalendarDay(date: string, day: MarketCalendarDay): void {
  if (date) marketCalendar.set(date, day)
}

function inSession(s: MarketCalendarSession | null, nowMs: number): boolean {
  if (!s) return false
  const start = Date.parse(s.start)
  const end = Date.parse(s.end)
  return Number.isFinite(start) && Number.isFinite(end) && nowMs >= start && nowMs < end
}

/**
 * Session phase from the authoritative calendar for `now`'s ET date, or null
 * when we have no calendar entry for that day (callers fall back to clock).
 */
function calendarPhase(now: Date): SessionPhase | null {
  const day = marketCalendar.get(todayET(now))
  if (!day) return null
  if (!day.isOpen) return 'CLOSED'
  const nowMs = now.getTime()
  if (inSession(day.regular, nowMs)) return 'RTH'
  if (inSession(day.pre, nowMs)) return 'PRE'
  if (inSession(day.post, nowMs)) return 'POST'
  return 'CLOSED'
}

/**
 * US regular-session hours only: Mon–Fri, 09:30–16:00 ET. Pre/post market and
 * holidays count as closed. Used to gate background polling so we don't burn
 * requests overnight or on weekends. Holidays aren't excluded — best-effort.
 */
export function isMarketOpen(now: Date = new Date()): boolean {
  const cal = calendarPhase(now)
  if (cal != null) return cal === 'RTH'
  const parts = TIME_FMT.formatToParts(now)
  const weekday = parts.find((p) => p.type === 'weekday')?.value
  const hour = Number(parts.find((p) => p.type === 'hour')?.value)
  const minute = Number(parts.find((p) => p.type === 'minute')?.value)
  if (!weekday || !Number.isFinite(hour) || !Number.isFinite(minute)) return false
  if (weekday === 'Sat' || weekday === 'Sun') return false
  const mins = hour * 60 + minute
  return mins >= 9 * 60 + 30 && mins < 16 * 60
}

/**
 * "YYYY-MM-DD" for the current calendar date in America/New_York. Used to
 * compare against option expiration strings (which are date-only) so the same
 * UTC instant doesn't read as "tomorrow" for west-coast users at 21:00 PT.
 */
export function todayET(now: Date = new Date()): string {
  return DATE_FMT.format(now)
}

/**
 * Parses "HH:MM" (24h ET) and returns true if the current ET wall clock is at
 * or past it. Empty/invalid input returns false so a blank setting = disabled.
 */
export function isPastCutoffET(cutoff: string | null | undefined, now: Date = new Date()): boolean {
  if (!cutoff) return false
  const m = /^(\d{1,2}):(\d{2})$/.exec(cutoff.trim())
  if (!m) return false
  const h = Number(m[1])
  const min = Number(m[2])
  if (h < 0 || h > 23 || min < 0 || min > 59) return false
  const parts = TIME_FMT.formatToParts(now)
  const hour = Number(parts.find((p) => p.type === 'hour')?.value)
  const minute = Number(parts.find((p) => p.type === 'minute')?.value)
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return false
  return hour * 60 + minute >= h * 60 + min
}

/**
 * Re-checks every 30s so polling pauses automatically at 16:00 ET and resumes
 * at 09:30 ET the next session, without the user having to refresh.
 */
export function useIsMarketOpen(): boolean {
  const [open, setOpen] = useState<boolean>(() => isMarketOpen())
  useEffect(() => {
    const id = window.setInterval(() => setOpen(isMarketOpen()), 30_000)
    return () => window.clearInterval(id)
  }, [])
  return open
}

/**
 * Equity trading session phase, ET wall clock:
 *   PRE    04:00–09:30  (extended — marketable LIMIT orders)
 *   RTH    09:30–16:00  (regular — MARKET orders)
 *   POST   16:00–20:00  (extended — marketable LIMIT orders)
 *   CLOSED everything else, incl. weekends
 * Clock-based only — holidays read as RTH and the order queues at the broker.
 */
export type SessionPhase = 'PRE' | 'RTH' | 'POST' | 'CLOSED'

export function getSessionPhase(now: Date = new Date()): SessionPhase {
  const cal = calendarPhase(now)
  if (cal != null) return cal
  const parts = TIME_FMT.formatToParts(now)
  const weekday = parts.find((p) => p.type === 'weekday')?.value
  const hour = Number(parts.find((p) => p.type === 'hour')?.value)
  const minute = Number(parts.find((p) => p.type === 'minute')?.value)
  if (!weekday || !Number.isFinite(hour) || !Number.isFinite(minute)) return 'CLOSED'
  if (weekday === 'Sat' || weekday === 'Sun') return 'CLOSED'
  const mins = (hour === 24 ? 0 : hour) * 60 + minute
  if (mins >= 4 * 60 && mins < 9 * 60 + 30) return 'PRE'
  if (mins >= 9 * 60 + 30 && mins < 16 * 60) return 'RTH'
  if (mins >= 16 * 60 && mins < 20 * 60) return 'POST'
  return 'CLOSED'
}

/**
 * Refetch interval for portfolio pollers: 5s while any equity session is
 * live (Mon–Fri 04:00–20:00 ET), 60s overnight/weekends when balances and
 * positions can't change. Each call is a live Schwab round-trip server-side,
 * so off-hours polling at trading cadence is pure waste. Re-evaluated by
 * react-query on every tick — pass directly as `refetchInterval`.
 */
export function portfolioPollMs(): number {
  return getSessionPhase() !== 'CLOSED' ? 5_000 : 60_000
}

export function phaseLabel(phase: SessionPhase): string {
  switch (phase) {
    case 'PRE': return 'Pre-market · limit'
    case 'RTH': return 'Regular · market'
    case 'POST': return 'After-hours · limit'
    case 'CLOSED': return 'Closed · queues at open'
  }
}

/** Re-checks every 30s, mirroring useIsMarketOpen. */
export function useSessionPhase(): SessionPhase {
  const [phase, setPhase] = useState<SessionPhase>(() => getSessionPhase())
  useEffect(() => {
    const id = window.setInterval(() => setPhase(getSessionPhase()), 30_000)
    return () => window.clearInterval(id)
  }, [])
  return phase
}
